/**
 * Auto-apply same-product pack promotions in the POS cart.
 *
 * Example: promo "4 por $1000" on a $300 cookie.
 * - 3 scans → 3 × $300 = $900
 * - 4th scan → 1 pack at $1000
 * - 5 scans → 1 pack + 1 unit = $1300
 *
 * Only promotions whose detail rows share the same pack size (>= 2) are
 * treated as N-for-price options (flavors). Mixed-qty combos are skipped.
 */

import { canonProductName } from './productNameCanon.js';

const sameId = (a, b) => Number(a) === Number(b);

function parseMonto(value) {
    const n = Math.round(Number(value));
    return Number.isFinite(n) ? n : 0;
}

function parseQty(value) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
}

function productIdOf(item) {
    const n = parseInt(item?.producto_id ?? item?.id, 10);
    return Number.isFinite(n) ? n : null;
}

/**
 * "4 por 1000", "4x$1.000", "4 un por 990" → 4.
 * Does not match product names like "MCKAY LIMON 40G".
 */
export function inferPackQtyFromText(...texts) {
    const text = texts.filter(Boolean).join(' ');
    const patterns = [
        /(\d+)\s*(?:un(?:idades?)?\.?)?\s*(?:por|x)\s*\$?\s*[\d.]+/i,
        /(\d+)\s*(?:un(?:idades?)?\.?)?\s*(?:por|x)\b/i,
    ];
    for (const re of patterns) {
        const m = text.match(re);
        if (!m) continue;
        const n = parseInt(m[1], 10);
        if (n >= 2 && n <= 24) return n;
    }
    return null;
}

/** Snapshot used to rebuild leftover unit lines after packing. */
export function snapshotProduct(item) {
    if (!item) return null;
    if (item.auto_product_snapshot) return { ...item.auto_product_snapshot };
    const { autoApplied, auto_product_id, auto_pack_qty, auto_product_snapshot, isPromo, promocion_id, promo_selected_item, ...rest } = item;
    return {
        ...rest,
        id: item.id,
        nombre: item.nombre,
        valor_producto: item.valor_producto ?? item.price,
        price: parseMonto(item.valor_producto ?? item.price),
        stock_actual: item.stock_actual,
    };
}

/**
 * Map productId → pack rules that save money vs unit price.
 */
export function extractPackRules(promotions) {
    const byProduct = {};
    for (const promo of promotions || []) {
        const rawItems = promo.items;
        let items = [];
        try {
            items = Array.isArray(rawItems)
                ? rawItems
                : (typeof rawItems === 'string' ? JSON.parse(rawItems || '[]') : []);
        } catch {
            items = [];
        }
        if (!items.length) continue;

        const monto = parseMonto(promo.monto);
        if (monto <= 0) continue;

        const qtyByProduct = new Map();
        const unitByProduct = new Map();
        for (const item of items) {
            const productId = productIdOf(item);
            if (productId == null) continue;
            const qty = parseQty(item.cantidad ?? item.quantity);
            qtyByProduct.set(productId, (qtyByProduct.get(productId) || 0) + qty);
            const unit = parseMonto(item.valor_producto ?? item.valor_producto);
            if (unit > 0) unitByProduct.set(productId, unit);
        }
        if (!qtyByProduct.size) continue;

        const qtys = [...qtyByProduct.values()];
        const allSame = qtys.every((q) => q === qtys[0]);
        const inferred = inferPackQtyFromText(promo.nombre, promo.descripcion);
        let packQty = null;

        if (allSame && qtys[0] >= 2) {
            packQty = qtys[0];
        } else if (inferred) {
            packQty = inferred;
        } else if (qtyByProduct.size === 1 && qtys[0] >= 2) {
            packQty = qtys[0];
        } else if (allSame && qtys[0] === 1) {
            // Qty 1 per flavor (default in the promo form). Infer N as the
            // smallest pack that is cheaper than buying N units — but only if
            // every product with a known unit price agrees on the same N.
            const guessed = [];
            for (const productId of qtyByProduct.keys()) {
                const unit = unitByProduct.get(productId) || 0;
                if (unit <= 0) continue;
                let n = null;
                for (let k = 2; k <= 12; k++) {
                    if (unit * k > monto) {
                        n = k;
                        break;
                    }
                }
                if (n) guessed.push(n);
            }
            if (guessed.length && guessed.every((g) => g === guessed[0])) {
                packQty = guessed[0];
            }
        }
        if (!packQty) continue;

        for (const [productId, listedQty] of qtyByProduct) {
            const unit = unitByProduct.get(productId) || 0;
            if (unit > 0 && unit * packQty <= monto) continue;
            if (!byProduct[productId]) byProduct[productId] = [];
            const exists = byProduct[productId].some((r) => r.promoId === promo.id && r.qty === packQty);
            if (exists) continue;
            const rule = {
                promoId: promo.id,
                nombre: promo.nombre || 'Promoción',
                qty: packQty,
                monto,
                listedQty,
            };
            byProduct[productId].push(rule);
            const listed = items.find((i) => productIdOf(i) === productId);
            const c = canonProductName(listed?.nombre);
            if (c) {
                if (!byProduct._byCanon) byProduct._byCanon = {};
                if (!byProduct._byCanon[c]) byProduct._byCanon[c] = [];
                if (!byProduct._byCanon[c].some((r) => r.promoId === rule.promoId && r.qty === rule.qty)) {
                    byProduct._byCanon[c].push(rule);
                }
            }
        }
    }
    return byProduct;
}

export function countProductUnits(cart, productId) {
    let n = 0;
    for (const item of cart || []) {
        if (item.autoApplied && sameId(item.auto_product_id, productId)) {
            n += (item.quantity || 0) * (item.auto_pack_qty || 1);
        } else if (!item.isPromo && !item.venta_por_peso && sameId(item.id, productId)) {
            n += item.quantity || 0;
        }
    }
    return n;
}

/**
 * Lowest-cost way to buy `totalUnits` using unit price and pack rules.
 */
export function optimalPack(totalUnits, unitPrice, rules) {
    const n = Math.max(0, Math.floor(Number(totalUnits) || 0));
    const unit = Math.max(0, parseMonto(unitPrice));
    const list = Array.isArray(rules) ? rules : [];

    const cost = Array(n + 1).fill(Infinity);
    const via = Array(n + 1).fill(null);
    cost[0] = 0;

    for (let i = 1; i <= n; i++) {
        cost[i] = cost[i - 1] + unit;
        via[i] = { kind: 'unit' };
        for (const rule of list) {
            if (i < rule.qty) continue;
            const cand = cost[i - rule.qty] + rule.monto;
            if (cand < cost[i]) {
                cost[i] = cand;
                via[i] = { kind: 'promo', rule };
            }
        }
    }

    const packCounts = new Map();
    let remainder = 0;
    let i = n;
    while (i > 0) {
        const step = via[i];
        if (!step || step.kind === 'unit') {
            remainder += 1;
            i -= 1;
        } else {
            const key = String(step.rule.promoId);
            const rec = packCounts.get(key) || { rule: step.rule, count: 0 };
            rec.count += 1;
            packCounts.set(key, rec);
            i -= step.rule.qty;
        }
    }

    return { remainder, packs: [...packCounts.values()] };
}

function linesForProduct(productId, totalUnits, snapshot, rules) {
    const unitPrice = parseMonto(snapshot?.valor_producto ?? snapshot?.price);
    const plan = optimalPack(totalUnits, unitPrice, rules);
    const lines = [];

    for (const { rule, count } of plan.packs) {
        if (count < 1) continue;
        lines.push({
            id: `promo-auto-${rule.promoId}-${productId}`,
            isPromo: true,
            autoApplied: true,
            promocion_id: rule.promoId,
            nombre: `Promo: ${rule.nombre}`,
            price: rule.monto,
            quantity: count,
            valor_producto: rule.monto,
            promo_selected_item: { producto_id: productId, cantidad: rule.qty },
            auto_product_id: productId,
            auto_pack_qty: rule.qty,
            auto_product_snapshot: snapshot,
        });
    }

    if (plan.remainder > 0 && snapshot) {
        lines.push({
            ...snapshot,
            id: productId,
            isPromo: false,
            autoApplied: false,
            quantity: plan.remainder,
            price: unitPrice,
        });
    }

    return lines;
}

function prevAutoCounts(cart) {
    const map = {};
    for (const item of cart || []) {
        if (!item.autoApplied) continue;
        const key = `${item.promocion_id}:${item.auto_product_id}`;
        map[key] = (map[key] || 0) + (item.quantity || 0);
    }
    return map;
}

function lookupRules(rulesByProduct, productId, nombre) {
    const byId = rulesByProduct[productId] || rulesByProduct[Number(productId)];
    if (byId?.length) return byId;
    const c = canonProductName(nombre);
    const byCanon = rulesByProduct._byCanon;
    return (c && byCanon && byCanon[c]) || [];
}

/**
 * Rebuild regular + auto-pack lines for products that have pack rules.
 * Manually added promo lines (from a loaded preventa) are left untouched.
 */
export function applyAutoPacks(cart, promotions) {
    const rulesByProduct = extractPackRules(promotions);
    const hasIdRules = Object.keys(rulesByProduct).some((k) => k !== '_byCanon');
    const hasCanonRules = rulesByProduct._byCanon && Object.keys(rulesByProduct._byCanon).length > 0;
    if (!cart?.length || (!hasIdRules && !hasCanonRules)) {
        return { cart: cart || [], newlyApplied: [] };
    }

    const before = prevAutoCounts(cart);
    const totals = {};
    const snapshots = {};
    const rulesForPid = {};

    cart.forEach((item) => {
        if (item.autoApplied) {
            const pid = Number(item.auto_product_id);
            const snap = snapshots[pid] || snapshotProduct(item);
            const rules = lookupRules(rulesByProduct, pid, snap?.nombre);
            if (!rules.length) return;
            totals[pid] = (totals[pid] || 0) + (item.quantity || 0) * (item.auto_pack_qty || 1);
            if (!snapshots[pid]) snapshots[pid] = snap;
            rulesForPid[pid] = rules;
        } else if (!item.isPromo && !item.venta_por_peso) {
            const pid = Number(item.id);
            const rules = lookupRules(rulesByProduct, pid, item.nombre);
            if (!rules.length) return;
            totals[pid] = (totals[pid] || 0) + (item.quantity || 0);
            snapshots[pid] = snapshotProduct(item);
            rulesForPid[pid] = rules;
        }
    });

    const rebuilt = {};
    for (const [pidStr, total] of Object.entries(totals)) {
        const pid = Number(pidStr);
        rebuilt[pid] = linesForProduct(pid, total, snapshots[pid], rulesForPid[pid] || lookupRules(rulesByProduct, pid, snapshots[pid]?.nombre));
    }

    const used = new Set();
    const next = [];
    for (const item of cart) {
        const pid = item.autoApplied
            ? Number(item.auto_product_id)
            : (!item.isPromo && !item.venta_por_peso ? Number(item.id) : null);
        if (pid != null && rebuilt[pid]) {
            if (!used.has(pid)) {
                used.add(pid);
                next.push(...rebuilt[pid]);
            }
            continue;
        }
        next.push(item);
    }

    const newlyApplied = [];
    const after = prevAutoCounts(next);
    for (const [key, qty] of Object.entries(after)) {
        const added = qty - (before[key] || 0);
        if (added <= 0) continue;
        const [promoId, productId] = key.split(':');
        const line = next.find((i) => i.autoApplied && String(i.promocion_id) === promoId && String(i.auto_product_id) === productId);
        if (!line) continue;
        newlyApplied.push({
            nombre: line.nombre,
            monto: line.price,
            packQty: line.auto_pack_qty,
            packs: added,
        });
    }

    return { cart: next, newlyApplied, newlyApplied };
}

export function setProductUnits(cart, productId, newTotal, snapshot) {
    const others = (cart || []).filter((item) => {
        if (item.autoApplied && sameId(item.auto_product_id, productId)) return false;
        if (!item.isPromo && !item.venta_por_peso && sameId(item.id, productId)) return false;
        return true;
    });
    const total = Math.max(0, Math.floor(Number(newTotal) || 0));
    if (total <= 0) return others;
    const snap = snapshot || snapshotProduct(cart.find((i) => sameId(i.id, productId) || sameId(i.auto_product_id, productId)));
    if (!snap) return others;
    return [
        ...others,
        {
            ...snap,
            id: productId,
            isPromo: false,
            quantity: total,
            price: parseMonto(snap.valor_producto ?? snap.price),
        },
    ];
}
